import { create } from "zustand";
import { getSocket } from "@/lib/socketClient";
import { listNotificationsApi, markAllNotificationsReadApi, markNotificationReadApi } from "@/portals/patient/api/notificationsApi";
import { usePatientAuthStore } from "@/stores/patientAuthStore";

export type NotificationType =
  | "CAREGIVER_INVITE_RECEIVED"
  | "CAREGIVER_INVITE_ACCEPTED"
  | "CAREGIVER_INVITE_DECLINED"
  | "CAREGIVER_INVITE_CANCELLED"
  | "ACCESS_REQUEST_RECEIVED"
  | "ACCESS_REQUEST_APPROVED"
  | "ACCESS_REQUEST_DECLINED"
  | "CAREGIVER_ACCESS_REVOKED"
  | "CAREGIVER_ACCESS_UPDATED"
  | "ACCESS_CATEGORY_REQUESTED"
  | "ACCESS_CATEGORY_APPROVED"
  | "ACCESS_CATEGORY_DECLINED"
  | "CAREGIVER_ACCESS_EXPIRING_SOON";

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: { relationshipId?: string; categoryRequestId?: string } | null;
  isRead: boolean;
  createdAt: string;
}

interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  fetch: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Drops a notification from the list entirely — used once its inline
   * Approve/Deny action has resolved it, so it doesn't linger looking
   * actionable when it no longer is. */
  remove: (id: string) => void;
}

/**
 * Global, un-scoped like the auth stores (not a React Context) — the bell
 * appears in both the patient and caregiver portal headers and needs the
 * exact same live state regardless of which one currently has it mounted.
 */
export const useNotificationsStore = create<NotificationsState>((set, get) => {
  getSocket().on("notification:new", (incoming: AppNotification) => {
    set((state) => ({
      notifications: [incoming, ...state.notifications],
      unreadCount: state.unreadCount + (incoming.isRead ? 0 : 1),
    }));
  });

  let lastToken: string | null = null;
  usePatientAuthStore.subscribe((state) => {
    if (state.accessToken === lastToken) return;
    lastToken = state.accessToken;
    if (state.accessToken) {
      void get().fetch();
    } else {
      set({ notifications: [], unreadCount: 0 });
    }
  });

  return {
    notifications: [],
    unreadCount: 0,
    isLoading: false,

    fetch: async () => {
      set({ isLoading: true });
      const { data } = await listNotificationsApi();
      set({ notifications: data?.data ?? [], unreadCount: data?.unreadCount ?? 0, isLoading: false });
    },

    markRead: async (id: string) => {
      const target = get().notifications.find((n) => n.id === id);
      if (!target || target.isRead) return;
      set((state) => ({
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
      await markNotificationReadApi(id);
    },

    markAllRead: async () => {
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
        unreadCount: 0,
      }));
      await markAllNotificationsReadApi();
    },

    remove: (id: string) => {
      set((state) => {
        const target = state.notifications.find((n) => n.id === id);
        return {
          notifications: state.notifications.filter((n) => n.id !== id),
          unreadCount: Math.max(0, state.unreadCount - (target && !target.isRead ? 1 : 0)),
        };
      });
    },
  };
});
